import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      // Normal logic for adding to cart
      const existingItemIndex = state.items.findIndex(
        item => 
          item.id === action.payload.id &&
          JSON.stringify(item.selectedRAM) === JSON.stringify(action.payload.selectedRAM) &&
          JSON.stringify(item.selectedStorage) === JSON.stringify(action.payload.selectedStorage)
      );

      if (existingItemIndex >= 0) {
        // For regular items, increment quantity
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex].quantity += action.payload.quantity;
        return {
          ...state,
          items: updatedItems
        };
      } else {
        return {
          ...state,
          items: [...state.items, { ...action.payload, cartId: Date.now() }]
        };
      }

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.cartId !== action.payload.cartId)
      };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.cartId === action.payload.cartId
            ? { ...item, quantity: Math.max(1, action.payload.quantity) }
            : item
        )
      };

    case 'APPLY_COUPON':
      return {
        ...state,
        appliedCoupon: action.payload
      };

    case 'REMOVE_COUPON':
      return {
        ...state,
        appliedCoupon: null
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        appliedCoupon: null
      };

    default:
      return state;
  }
};

const initialState = {
  items: [],
  appliedCoupon: null
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [isCartPopupVisible, setIsCartPopupVisible] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('nexawave-cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        parsedCart.items.forEach(item => {
          dispatch({ type: 'ADD_TO_CART', payload: item });
        });
        if (parsedCart.appliedCoupon) {
          dispatch({ type: 'APPLY_COUPON', payload: parsedCart.appliedCoupon });
        }
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('nexawave-cart', JSON.stringify(state));
  }, [state]);

  const addToCart = (product, selectedRAM = null, selectedStorage = null, quantity = 1) => {
    const cartItem = {
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      originalPrice: product.originalPrice,
      imageUrls: product.imageUrls,
      selectedRAM,
      selectedStorage,
      quantity,
      baseSpecs: {
        processor: product.processor,
        ramSize: product.ramSize,
        storageSize: product.storageSize,
        storageType: product.storageType,
        screenSize: product.screenSize
      }
    };

    dispatch({ type: 'ADD_TO_CART', payload: cartItem });
    setIsCartPopupVisible(true);

    // Track Meta Pixel AddToCart event
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: product.name,
        content_ids: [product.id],
        content_type: 'product',
        value: product.price * quantity,
        currency: 'INR'
      });
    }
  };

  const removeFromCart = (cartId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: { cartId } });
  };

  const updateQuantity = (cartId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { cartId, quantity } });
  };

  const applyCoupon = (coupon) => {
    dispatch({ type: 'APPLY_COUPON', payload: coupon });
  };

  const removeCoupon = () => {
    dispatch({ type: 'REMOVE_COUPON' });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => {
      const itemPrice = getItemTotalPrice(item);
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  const getCartCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  const getItemTotalPrice = (item) => {
    let itemPrice = item.price;
    if (item.selectedRAM) itemPrice += item.selectedRAM.priceIncrease;
    if (item.selectedStorage) itemPrice += item.selectedStorage.priceIncrease;
    return itemPrice;
  };

  const getDiscountAmount = () => {
    if (!state.appliedCoupon) return 0;
    
    const discountableAmount = getCartTotal();
    
    if (state.appliedCoupon.type === 'percentage') {
      return (discountableAmount * state.appliedCoupon.value) / 100;
    } else {
      return Math.min(state.appliedCoupon.value, discountableAmount);
    }
  };

  const getFinalTotal = () => {
    const regularTotal = getCartTotal();
    const discountAmount = getDiscountAmount();
    return Math.max(0, regularTotal - discountAmount);
  };

  const value = {
    ...state,
    addToCart,
    removeFromCart,
    updateQuantity,
    applyCoupon,
    removeCoupon,
    clearCart,
    getCartTotal,
    getCartCount,
    getDiscountAmount,
    getFinalTotal,
    getItemTotalPrice,
    isCartPopupVisible,
    setCartPopupVisible: setIsCartPopupVisible
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;